import { useState, useEffect } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import {
  usePersonalBudgets,
  type BudgetWithSpent,
  type BudgetPeriodType,
  type CreateBudgetInput,
  type UpdateBudgetInput,
} from '@/hooks';
import { usePersonalCategories } from '@/hooks';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
} from 'lucide-react';

type DialogMode = 'create' | 'edit' | null;

interface BudgetFormState {
  category_id: string;
  period_type: BudgetPeriodType;
  limit_amount: string;
  alert_threshold_warning: string;
  alert_threshold_critical: string;
  custom_start_date: string;
  custom_end_date: string;
}

const EMPTY_FORM: BudgetFormState = {
  category_id: '',
  period_type: 'monthly',
  limit_amount: '',
  alert_threshold_warning: '80',
  alert_threshold_critical: '100',
  custom_start_date: '',
  custom_end_date: '',
};

const PERIOD_LABELS: Record<BudgetPeriodType, string> = {
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  monthly: 'Monthly',
  custom: 'Custom',
};

function formatCurrency(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

export function BudgetManagementPage() {
  const {
    budgets,
    isLoading,
    createBudget,
    updateBudget,
    deleteBudget,
  } = usePersonalBudgets();

  const { expenseCategories } = usePersonalCategories({ categoryType: 'expense' });

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [editingBudget, setEditingBudget] = useState<BudgetWithSpent | null>(null);
  const [form, setForm] = useState<BudgetFormState>(EMPTY_FORM);

  // Categories that already have a budget (for filtering the dropdown)
  const budgetedCategoryIds = new Set(budgets.map((b) => b.category_id));

  // Auto-open create dialog if ?action=add is in URL
  const searchParams = useSearch({ strict: false }) as Record<string, string>;
  useEffect(() => {
    if (searchParams.action === 'add') {
      setForm(EMPTY_FORM);
      setEditingBudget(null);
      setDialogMode('create');
    }
  }, []);

  function openCreateDialog() {
    setForm(EMPTY_FORM);
    setEditingBudget(null);
    setDialogMode('create');
  }

  function openEditDialog(budget: BudgetWithSpent) {
    setForm({
      category_id: budget.category_id,
      period_type: budget.period_type,
      limit_amount: parseFloat(budget.limit_amount).toString(),
      alert_threshold_warning: budget.alert_threshold_warning.toString(),
      alert_threshold_critical: budget.alert_threshold_critical.toString(),
      custom_start_date: budget.custom_start_date || '',
      custom_end_date: budget.custom_end_date || '',
    });
    setEditingBudget(budget);
    setDialogMode('edit');
  }

  function closeDialog() {
    setDialogMode(null);
    setEditingBudget(null);
  }

  async function handleSave() {
    const limitAmount = parseFloat(form.limit_amount);
    if (!form.limit_amount || isNaN(limitAmount) || limitAmount <= 0) {
      toast({ variant: 'destructive', title: 'Error', description: 'Budget amount must be a positive number.' });
      return;
    }

    try {
      if (dialogMode === 'create') {
        if (!form.category_id) {
          toast({ variant: 'destructive', title: 'Error', description: 'Please select a category.' });
          return;
        }
        const input: CreateBudgetInput = {
          category_id: form.category_id,
          period_type: form.period_type,
          limit_amount: limitAmount,
          alert_threshold_warning: parseInt(form.alert_threshold_warning) || 80,
          alert_threshold_critical: parseInt(form.alert_threshold_critical) || 100,
          custom_start_date: form.period_type === 'custom' ? form.custom_start_date || undefined : undefined,
          custom_end_date: form.period_type === 'custom' ? form.custom_end_date || undefined : undefined,
        };
        await createBudget.mutateAsync(input);
        toast({ title: 'Budget created', description: `Budget of ${formatCurrency(limitAmount)} has been set.` });
      } else if (dialogMode === 'edit' && editingBudget) {
        const input: UpdateBudgetInput & { id: string } = {
          id: editingBudget.id,
          period_type: form.period_type,
          limit_amount: limitAmount,
          alert_threshold_warning: parseInt(form.alert_threshold_warning) || 80,
          alert_threshold_critical: parseInt(form.alert_threshold_critical) || 100,
          custom_start_date: form.period_type === 'custom' ? form.custom_start_date || null : null,
          custom_end_date: form.period_type === 'custom' ? form.custom_end_date || null : null,
        };
        await updateBudget.mutateAsync(input);
        toast({ title: 'Budget updated', description: 'Your budget has been saved.' });
      }
      closeDialog();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      toast({ variant: 'destructive', title: 'Error', description: message });
    }
  }

  async function handleDelete(budget: BudgetWithSpent) {
    try {
      await deleteBudget.mutateAsync(budget.id);
      toast({ title: 'Budget deleted', description: `Budget for "${budget.category_name}" has been removed.` });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      toast({ variant: 'destructive', title: 'Error', description: message });
    }
  }

  const isSaving = createBudget.isPending || updateBudget.isPending;

  // Flatten expense categories for the dropdown (include children)
  const flatExpenseCategories = expenseCategories.reduce<{ id: string; name: string }[]>((acc, cat) => {
    acc.push({ id: cat.id, name: cat.name });
    if (cat.children) {
      for (const child of cat.children) {
        acc.push({ id: child.id, name: `  ${cat.name} > ${child.name}` });
      }
    }
    return acc;
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main className="flex-1 py-8">
        <div className="container max-w-3xl">
          {/* Back link */}
          <Link
            to="/app/personal/dashboard"
            className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Personal Dashboard
          </Link>

          {/* Page header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl font-bold">Budgets</h1>
              <p className="mt-1 text-muted-foreground">
                Set spending limits for your expense categories
              </p>
            </div>
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Create Budget
            </Button>
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="mt-12 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Budget list */}
          {!isLoading && budgets.length === 0 && (
            <div className="mt-12 rounded-card border border-dashed bg-muted/50 p-8 text-center">
              <h3 className="font-display text-lg font-semibold">No budgets yet</h3>
              <p className="mt-2 text-muted-foreground">
                Create a budget to start tracking your spending against limits.
              </p>
            </div>
          )}

          {!isLoading && budgets.length > 0 && (
            <div className="mt-8 rounded-card border bg-card p-6">
              <div className="divide-y">
                {budgets.map((budget) => (
                  <BudgetRow
                    key={budget.id}
                    budget={budget}
                    onEdit={() => openEditDialog(budget)}
                    onDelete={() => handleDelete(budget)}
                  />
                ))}
              </div>

              {/* Total summary */}
              <div className="mt-4 border-t-2 pt-4">
                <div className="flex items-center justify-between text-sm font-semibold">
                  <span>Total Budget</span>
                  <div className="flex gap-8">
                    <span>{formatCurrency(budgets.reduce((sum, b) => sum + parseFloat(b.limit_amount), 0))}</span>
                    <span className="text-muted-foreground">
                      Spent: {formatCurrency(budgets.reduce((sum, b) => sum + b.spent_amount, 0))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogMode !== null} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'create' ? 'Create Budget' : 'Edit Budget'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Category (create only) */}
            {dialogMode === 'create' && (
              <div className="space-y-2">
                <Label htmlFor="budget-category">Category</Label>
                <select
                  id="budget-category"
                  value={form.category_id}
                  onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}
                  className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Select a category...</option>
                  {flatExpenseCategories
                    .filter((c) => !budgetedCategoryIds.has(c.id))
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
              </div>
            )}

            {/* Category name (edit — read only) */}
            {dialogMode === 'edit' && editingBudget && (
              <div className="space-y-2">
                <Label>Category</Label>
                <div className="flex h-10 items-center rounded-lg border bg-muted/50 px-3 text-sm">
                  {editingBudget.category_name}
                </div>
              </div>
            )}

            {/* Period Type */}
            <div className="space-y-2">
              <Label>Period</Label>
              <div className="flex gap-3">
                {(['weekly', 'biweekly', 'monthly', 'custom'] as const).map((type) => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="period_type"
                      value={type}
                      checked={form.period_type === type}
                      onChange={() => setForm((f) => ({ ...f, period_type: type }))}
                      className="accent-sapling-primary-100"
                    />
                    <span className="text-sm">{PERIOD_LABELS[type]}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Custom dates */}
            {form.period_type === 'custom' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="budget-start">Start Date</Label>
                  <Input
                    id="budget-start"
                    type="date"
                    value={form.custom_start_date}
                    onChange={(e) => setForm((f) => ({ ...f, custom_start_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budget-end">End Date</Label>
                  <Input
                    id="budget-end"
                    type="date"
                    value={form.custom_end_date}
                    onChange={(e) => setForm((f) => ({ ...f, custom_end_date: e.target.value }))}
                  />
                </div>
              </div>
            )}

            {/* Limit Amount */}
            <div className="space-y-2">
              <Label htmlFor="budget-amount">Budget Amount ($)</Label>
              <Input
                id="budget-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={form.limit_amount}
                onChange={(e) => setForm((f) => ({ ...f, limit_amount: e.target.value }))}
                placeholder="0.00"
              />
            </div>

            {/* Thresholds */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="budget-warning">Warning at (%)</Label>
                <Input
                  id="budget-warning"
                  type="number"
                  min="1"
                  max="100"
                  value={form.alert_threshold_warning}
                  onChange={(e) => setForm((f) => ({ ...f, alert_threshold_warning: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="budget-critical">Over budget at (%)</Label>
                <Input
                  id="budget-critical"
                  type="number"
                  min="1"
                  max="200"
                  value={form.alert_threshold_critical}
                  onChange={(e) => setForm((f) => ({ ...f, alert_threshold_critical: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {dialogMode === 'create' ? 'Create' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// BudgetRow
// ---------------------------------------------------------------------------

interface BudgetRowProps {
  budget: BudgetWithSpent;
  onEdit: () => void;
  onDelete: () => void;
}

function BudgetRow({ budget, onEdit, onDelete }: BudgetRowProps) {
  const limitAmount = parseFloat(budget.limit_amount);
  const percentage = limitAmount > 0 ? (budget.spent_amount / limitAmount) * 100 : 0;
  const barWidth = Math.min(percentage, 100);

  const warningThreshold = budget.alert_threshold_warning;
  const criticalThreshold = budget.alert_threshold_critical;

  let barColor = 'bg-sapling-primary-100'; // green
  if (percentage >= criticalThreshold) {
    barColor = 'bg-sapling-error-100'; // red
  } else if (percentage >= warningThreshold) {
    barColor = 'bg-sapling-warning-100'; // yellow
  }

  return (
    <div className="flex items-center gap-4 py-3">
      {/* Color dot + name */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span
          className="h-3 w-3 shrink-0 rounded-full"
          style={{ backgroundColor: budget.category_color || '#6B7280' }}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{budget.category_name}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {PERIOD_LABELS[budget.period_type]}
            </span>
          </div>
          {/* Progress bar */}
          <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full rounded-full transition-all ${barColor}`}
              style={{ width: `${barWidth}%` }}
            />
          </div>
        </div>
      </div>

      {/* Amounts */}
      <div className="shrink-0 text-right text-sm">
        <div className="font-medium">{formatCurrency(limitAmount)}</div>
        <div className="text-muted-foreground">
          {formatCurrency(budget.spent_amount)} ({Math.round(percentage)}%)
        </div>
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
